import { Banner, Card } from "@shopify/polaris"
import { uniqueId } from "lodash-es"
import { useCallback, useEffect, useRef, useState } from "react"

import { Command, RuleItem, Validator } from "../utils"

// 添加一个command系统用于检测表单是否校验全部通过
const validCommand = new Command<
  (v?: any) => { id: string; error: string }
>()
/**
 * 全局表单校验方法
 * 如果执行的命令中没有错误文本则校验通过
 * @returns 校验结果
 */
export const validate = () => {
  const errorList = validCommand.run()
  return errorList.filter((error) => error.error).length === 0
}

export const validateV2 = (keys?: string[], scrollToError: boolean = true) => {
  const validList = keys
    ? validCommand.runByPatternsOrKeys(keys)
    : validCommand.run()
  const errorList = validList.filter((error) => error.error)
  const allPass = errorList.length === 0
  if (scrollToError && !allPass) {
    setTimeout(() => {
      const dom = document.getElementById(errorList[0].id)
      dom?.scrollIntoView({
        block: "center",
      })
    }, 0)
  }
  return {
    allPass,
    errorList,
  }
}

interface useValidateProps {
  rules?: RuleItem[]
  value?: any
  error?: any
  onFocus?(): void
  onBlur?(): void
  id?: string
  onlyRequired?: boolean
  onChange?(v, id): void
  onlyRequiredText?: string
  errorBannerClassName?: string
}
export const useValidate = (props: useValidateProps) => {
  const {
    value,
    error,
    onBlur,
    onFocus,
    onChange,
    id: originId,
    onlyRequired = false,
    onlyRequiredText,
    errorBannerClassName,
    rules = onlyRequired
      ? [
          {
            validator(_, val) {
              if (Array.isArray(val) && val.length === 0) {
                return onlyRequiredText || t("Required")
              }
              if (typeof val === "string" && val.trim() === "") {
                return onlyRequiredText || t("Required")
              }
              if (typeof val === "number" && val === 0) {
                return onlyRequiredText || t("Required")
              }
            },
          },
        ]
      : [],
  } = props
  const validator = useRef(new Validator(rules))
  const [errorText, setErrorText] = useState("")
  const [isFocus, setIsFocus] = useState(false)
  const idRef = useRef(`pp-form-${uniqueId()}`)

  const id = originId ?? idRef.current

  const validate = useCallback(
    (v = value) => {
      setIsFocus(false)
      const err = validator.current.run(v)
      setErrorText(err)
      return {
        id: id,
        error: err,
      }
    },
    [id, value],
  )

  useEffect(() => {
    // 自定义函数中可能存在其他依赖，所以不能缓存
    validator.current = new Validator(rules)
    if (rules.length === 0) {
      setErrorText("")
    }
  }, [rules])

  // 订阅者：挂载时订阅validate保存事件，离开是取消订阅
  useEffect(() => {
    if (rules.length) {
      validCommand.push(id, validate)
      return () => {
        validCommand.remove(id)
      }
    }
  }, [id, rules.length, validate])

  // 实时移除input上的errText
  useEffect(() => {
    if (rules.length) {
      const err = validator.current.run(value)
      if (!err) {
        setErrorText("")
      }
    }
  }, [value, rules.length])

  function handleFocus() {
    setIsFocus(true)
    onFocus?.()
  }

  function handleBlur() {
    validate()
    onBlur?.()
  }

  function handleChange(v, id) {
    onChange?.(v, id)
    setErrorText("")
  }

  const hasError = !isFocus && (error || errorText)
  const errorBanner = (error || errorText) && (
    <div className="pp-remove-banner-shadow">
      <Card padding={"0"}>
        <Banner
          tone="critical"
          onDismiss={() => {
            setErrorText("")
          }}
        >
          {onlyRequiredText || t("Required")}
        </Banner>
      </Card>
    </div>
  )
  return {
    // 聚焦不显示错误，原生错误优先级高于自定义错误
    error: hasError,
    setErrorText,
    validate,
    id,
    isFocus,
    setIsFocus,
    handleFocus,
    handleBlur,
    handleChange,
    errorBanner:
      errorBannerClassName && errorBanner ? (
        <div className={errorBannerClassName}>{errorBanner}</div>
      ) : (
        errorBanner
      ),
  }
}
